import type { ApiResponse, PageResult, PlayerVideo, VideoMeta } from './types';

/**
 * 视频播放器API封装
 *
 * @author xiaojingge
 */
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || 'https://api.xiaojingge.com/api/api').replace(/\/$/, '');

function normalizeStreamUrl(streamUrl: string): string {
  try {
    const apiUrl = new URL(API_BASE_URL);
    const videoUrl = new URL(streamUrl, apiUrl.origin);
    if (videoUrl.host === apiUrl.host) {
      videoUrl.protocol = apiUrl.protocol;
    }
    return videoUrl.toString();
  } catch {
    return streamUrl;
  }
}

async function request<T>(path: string): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`);
  if (!response.ok) {
    throw new Error(`请求失败：HTTP ${response.status}`);
  }

  const json = (await response.json()) as ApiResponse<T>;
  if (json.code !== 200) {
    throw new Error(json.message || '接口返回异常');
  }
  return json.data;
}

function toSafeNumber(value: unknown, fallback = 0): number {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
}

export function getVideoMeta(): Promise<VideoMeta> {
  return request<VideoMeta>('/videos/meta');
}

export function getRandomVideo(params: { sourceId?: string; categoryId?: string }): Promise<PlayerVideo> {
  const search = new URLSearchParams();
  if (params.sourceId) {
    search.set('sourceId', params.sourceId);
  }
  if (params.categoryId) {
    search.set('categoryId', params.categoryId);
  }
  const query = search.toString();
  return request<PlayerVideo>(`/videos/random${query ? `?${query}` : ''}`).then((video) => ({
    ...video,
    streamUrl: normalizeStreamUrl(video.streamUrl),
  }));
}

export function getVideoPage(params: {
  current?: number;
  size?: number;
  sourceId?: string;
  categoryId?: string;
  keyword?: string;
}): Promise<PageResult<PlayerVideo>> {
  const search = new URLSearchParams();
  search.set('current', String(params.current ?? 1));
  search.set('size', String(params.size ?? 12));
  if (params.sourceId) {
    search.set('sourceId', params.sourceId);
  }
  if (params.categoryId) {
    search.set('categoryId', params.categoryId);
  }
  if (params.keyword?.trim()) {
    search.set('keyword', params.keyword.trim());
  }

  return request<PageResult<PlayerVideo>>(`/videos?${search.toString()}`).then((page) => ({
    ...page,
    current: toSafeNumber(page.current, params.current ?? 1),
    size: toSafeNumber(page.size, params.size ?? 12),
    total: toSafeNumber(page.total, 0),
    pages: toSafeNumber(page.pages, 0),
    records: page.records.map((video) => ({
      ...video,
      streamUrl: normalizeStreamUrl(video.streamUrl),
    })),
  }));
}
